/**
 * <mb-hero-video> — owns the hero's decorative background video.
 *
 * Data contract (set by sections/modern-hero-fullbleed.liquid):
 *   data-src-desktop — mp4 rendition for >= 750px
 *   data-src-mobile  — lighter rendition for phones
 * The <video> ships with no src and preload="none", so nothing downloads until
 * this element decides to play. The section's still image sits underneath and is
 * the LCP element; the video fades in over it once it can play.
 *
 * Playback is withheld when the visitor asked for reduced motion (WCAG 2.5.8),
 * when Data Saver is on, or on a 2g/3g-class connection — a silent backdrop is
 * never worth megabytes on a metered phone. The preference is re-checked live:
 * macOS and iOS toggle it without a reload.
 */
class MBHeroVideo extends HTMLElement {
  #motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  #phone = window.matchMedia('(max-width: 749px)');
  #cleanups = [];
  #armed = false;

  connectedCallback() {
    this.#on(this.#motion, 'change', () => this.#apply());

    // Start once the hero's still image has arrived. That image is the LCP
    // element, so by then LCP is sealed and the video can spend bandwidth freely.
    // Waiting for window load instead would hand the trigger to whichever
    // third-party script finishes last, holding the hero static for no gain.
    const still = this.closest('.mb-fbhero__media')?.querySelector('img');
    if (!still || still.complete) this.#idle();
    else {
      this.#on(still, 'load', () => this.#idle());
      this.#on(still, 'error', () => this.#idle());
    }
  }

  disconnectedCallback() {
    for (const off of this.#cleanups) off();
    this.#cleanups = [];
  }

  #on(target, event, handler) {
    target.addEventListener(event, handler);
    this.#cleanups.push(() => target.removeEventListener(event, handler));
  }

  #idle() {
    // A short breath so the decode does not land on top of script init, capped
    // low: this runs after LCP, so there is nothing left worth deferring for.
    const run = () => this.#apply();
    if ('requestIdleCallback' in window) requestIdleCallback(run, { timeout: 400 });
    else setTimeout(run, 100);
  }

  get #allowed() {
    if (this.#motion.matches) return false;
    const net = navigator.connection;
    if (net?.saveData) return false;
    if (net?.effectiveType && /2g|3g/.test(net.effectiveType)) return false;
    return true;
  }

  #apply() {
    const video = this.querySelector('video');
    if (!video) return;

    if (!this.#allowed) {
      video.pause?.();
      // Fade back to the still image rather than freezing on a mid-loop frame.
      video.removeAttribute('data-mb-playing');
      return;
    }

    if (!this.#armed) {
      const src = (this.#phone.matches && this.dataset.srcMobile) || this.dataset.srcDesktop;
      if (!src) return;
      this.#armed = true;
      video.addEventListener('playing', () => video.setAttribute('data-mb-playing', ''), { once: true });
      video.src = src;
    }

    // Autoplay can still be refused (low-power mode); the image stays, no error.
    video.play?.().catch(() => {});
  }
}

customElements.define('mb-hero-video', MBHeroVideo);
